/**
 * Expense categories and mileage rates, under Settings. Both are shared with
 * the phone, so a change here shows up there on its next load.
 *
 * This app never creates the default categories — the Reimburse app seeds them
 * on its first run. Two apps each seeding an empty collection could leave you
 * with every category twice.
 */

import { useMemo, useState } from "react";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CATEGORY_ICONS, CategoryIcon, categoryIconKey } from "@/components/ExpenseIcons";
import { useToast } from "@/context/ToastContext";
import {
  pbError, useCategoryMutations, useExpenseCategories, useExpenses, useMileageRates, useRateMutations,
} from "@/hooks/useExpenses";
import { dayOf, fmtDay, sortCategories, todayIso } from "@/lib/expenses";
import type { ExpenseCategory, MileageRate } from "@/lib/types";

import { Modal, Toggle, dangerBtn, ghostBtn, input, label, linkBtn, primaryBtn } from "./ui";

// ---- Categories ------------------------------------------------------------

export function CategorySettings() {
  const { data: cats = [] } = useExpenseCategories();
  const { reorder } = useCategoryMutations();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<ExpenseCategory> | null>(null);

  const sorted = sortCategories(cats);
  const live = sorted.filter((c) => !c.archived);
  const hidden = sorted.filter((c) => c.archived);

  async function move(i: number, d: number) {
    const j = i + d;
    if (j < 0 || j >= live.length) return;
    const order = [...live];
    [order[i], order[j]] = [order[j], order[i]];
    try { await reorder.mutateAsync(order); }
    catch (e) { toast("Couldn't reorder: " + pbError(e)); }
  }

  const row = (c: ExpenseCategory, i: number, list: ExpenseCategory[]) => (
    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid var(--tint2)" }}>
      <span style={{ color: "var(--ink3)", display: "inline-flex" }}><CategoryIcon name={categoryIconKey(c)} size={18} /></span>
      <span style={{ flex: 1, fontSize: 13.5 }}>
        {c.name}
        {c.is_mileage && <span style={{ fontSize: 11.5, color: "var(--accent)", marginLeft: 8 }}>mileage · auto-calculated</span>}
      </span>
      {!c.archived && list.length > 1 && (
        <>
          <button onClick={() => void move(i, -1)} disabled={i === 0} aria-label={`Move ${c.name} up`} style={arrow}>↑</button>
          <button onClick={() => void move(i, 1)} disabled={i === list.length - 1} aria-label={`Move ${c.name} down`} style={arrow}>↓</button>
        </>
      )}
      <button onClick={() => setEditing(c)} style={linkBtn}>Edit</button>
    </div>
  );

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
        How expenses are grouped on a report and its package, in this order. Shared with the Reimburse app.
      </div>
      {live.map((c, i) => row(c, i, live))}
      {cats.length === 0 && (
        <div style={{ fontSize: 12.5, color: "var(--muted)", padding: "8px 0" }}>
          None yet. The Reimburse app creates the standard set on its first run, or add your own.
        </div>
      )}
      <div style={{ paddingTop: 10, borderTop: "1px solid var(--tint2)" }}>
        <button onClick={() => setEditing({ icon: "receipt", is_mileage: false, archived: false })} style={ghostBtn}>Add category</button>
      </div>
      {hidden.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--muted)", margin: "16px 0 2px" }}>Archived — hidden from new expenses</div>
          {hidden.map((c, i) => row(c, i, hidden))}
        </>
      )}
      {editing && <CategoryModal draft={editing} count={cats.length} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CategoryModal({ draft, count, onClose }: { draft: Partial<ExpenseCategory>; count: number; onClose: () => void }) {
  const { saveCategory, deleteCategory } = useCategoryMutations();
  const { data: expenses = [] } = useExpenses();
  const toast = useToast();
  const [d, setD] = useState(draft);
  const [confirm, setConfirm] = useState(false);
  const inUse = useMemo(() => (draft.id ? expenses.filter((e) => e.category === draft.id).length : 0), [expenses, draft.id]);

  async function save() {
    const name = d.name?.trim();
    if (!name) { toast("Give the category a name"); return; }
    const data = { name, icon: categoryIconKey(d), is_mileage: !!d.is_mileage, archived: !!d.archived };
    try {
      await saveCategory.mutateAsync(d.id ? { id: d.id, ...data } : { ...data, sort: count + 1 });
      toast("Category saved");
      onClose();
    } catch (e) {
      toast("Couldn't save: " + pbError(e));
    }
  }

  return (
    <Modal title={d.id ? "Edit category" : "New category"} onClose={onClose}>
      <label style={label}>Name<input autoFocus value={d.name ?? ""} onChange={(e) => setD({ ...d, name: e.target.value })} style={input} placeholder="Tolls" /></label>
      <div style={label}>
        Icon
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
          {CATEGORY_ICONS.map((k) => {
            const on = categoryIconKey(d) === k;
            return (
              <button key={k} type="button" onClick={() => setD({ ...d, icon: k })} aria-label={k} title={k} style={{
                width: 38, height: 38, borderRadius: 9, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
                border: on ? "1.5px solid var(--accent)" : "1px solid var(--line2)",
                background: on ? "var(--accentTint)" : "var(--surface)", color: on ? "var(--accent)" : "var(--ink3)",
              }}><CategoryIcon name={k} size={19} /></button>
            );
          })}
        </div>
      </div>
      <Toggle on={!!d.is_mileage} onChange={(v) => setD({ ...d, is_mileage: v })}>Mileage category — amount is miles × the IRS rate</Toggle>
      <Toggle on={!!d.archived} onChange={(v) => setD({ ...d, archived: v })}>Archived — hidden from new expenses</Toggle>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        {d.id && <button onClick={() => setConfirm(true)} style={dangerBtn}>Delete</button>}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button onClick={() => void save()} style={primaryBtn}>Save</button>
      </div>

      {/* A category that expenses point at is archived instead, so their
          history keeps its label. */}
      {confirm && d.id && (
        <ConfirmDialog
          title={inUse ? `Archive “${draft.name}” instead?` : `Delete “${draft.name}”?`}
          message={inUse
            ? `${inUse} expense${inUse === 1 ? "" : "s"} still use it, so it can't be deleted. Archiving hides it from new expenses and keeps it on the old ones.`
            : "No expense uses it."}
          confirmLabel={inUse ? "Archive" : "Delete"}
          danger={!inUse}
          onConfirm={async () => {
            try {
              if (inUse) await saveCategory.mutateAsync({ id: d.id!, archived: true });
              else await deleteCategory.mutateAsync(d.id!);
              toast(inUse ? "Archived" : "Deleted");
              onClose();
            } catch (e) {
              toast("Couldn't update: " + pbError(e));
            }
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </Modal>
  );
}

const arrow: React.CSSProperties = {
  width: 26, height: 26, borderRadius: 6, border: "1px solid var(--line2)", background: "var(--surface)",
  color: "var(--ink3)", cursor: "pointer", fontSize: 12, lineHeight: 1,
};

// ---- Mileage rates ---------------------------------------------------------

export function MileageRateSettings() {
  const { data: rates = [] } = useMileageRates();
  const [editing, setEditing] = useState<Partial<MileageRate> | null>(null);
  const sorted = [...rates].sort((a, b) => dayOf(b.effective_date).localeCompare(dayOf(a.effective_date)));
  const today = todayIso();
  const current = sorted.find((r) => dayOf(r.effective_date) <= today);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
        A trip uses the newest rate in effect on its date, so trips already logged keep the rate they were worked out at.
        Add each year's IRS rates here if you log older trips.
      </div>
      {sorted.map((r) => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid var(--tint2)" }}>
          <span className="mono" style={{ fontSize: 13.5, width: 110 }}>${Number(r.rate).toFixed(3)}/mi</span>
          <span style={{ flex: 1, fontSize: 12.5, color: "var(--ink3)" }}>
            from {fmtDay(r.effective_date)}{r.note ? ` · ${r.note}` : ""}
            {current?.id === r.id && <span style={{ color: "var(--accent)", marginLeft: 8 }}>current</span>}
          </span>
          <button onClick={() => setEditing(r)} style={linkBtn}>Edit</button>
        </div>
      ))}
      {rates.length === 0 && <div style={{ fontSize: 12.5, color: "var(--warn)", padding: "8px 0" }}>No rates yet — mileage will calculate as $0.</div>}
      <div style={{ paddingTop: 10, borderTop: "1px solid var(--tint2)" }}>
        <button onClick={() => setEditing({ effective_date: today, rate: undefined, note: "" })} style={ghostBtn}>Add rate</button>
      </div>
      {editing && <RateModal draft={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function RateModal({ draft, onClose }: { draft: Partial<MileageRate>; onClose: () => void }) {
  const { saveRate, deleteRate } = useRateMutations();
  const toast = useToast();
  const [date, setDate] = useState(dayOf(draft.effective_date) || todayIso());
  const [rate, setRate] = useState(draft.rate != null ? String(draft.rate) : "");
  const [note, setNote] = useState(draft.note ?? "");
  const [confirm, setConfirm] = useState(false);

  async function save() {
    const n = Number(rate);
    if (!(n > 0)) { toast("Enter a rate like 0.76"); return; }
    if (!date) { toast("Pick the date it takes effect"); return; }
    try {
      await saveRate.mutateAsync({ id: draft.id, effective_date: date, rate: n, note: note.trim() });
      toast("Rate saved");
      onClose();
    } catch (e) {
      toast("Couldn't save: " + pbError(e));
    }
  }

  return (
    <Modal title={draft.id ? "Edit mileage rate" : "New mileage rate"} width={400} onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={label}>Effective from<input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} /></label>
        <label style={label}>Rate per mile<input type="number" step="0.001" min={0} value={rate} onChange={(e) => setRate(e.target.value)} style={input} placeholder="0.76" autoFocus /></label>
      </div>
      <label style={label}>Note<input value={note} onChange={(e) => setNote(e.target.value)} style={input} placeholder="IRS business rate — Jul 2026" /></label>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {draft.id && <button onClick={() => setConfirm(true)} style={dangerBtn}>Delete</button>}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button onClick={() => void save()} style={primaryBtn}>Save</button>
      </div>
      {confirm && draft.id && (
        <ConfirmDialog
          title="Delete this rate?"
          message={`Trips already logged keep the amount they were saved with. New trips dated from ${fmtDay(draft.effective_date)} will use the rate before it.`}
          onConfirm={async () => {
            try { await deleteRate.mutateAsync(draft.id!); toast("Rate deleted"); onClose(); }
            catch (e) { toast("Couldn't delete: " + pbError(e)); }
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </Modal>
  );
}
